import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './App';
import ThemeToggle from './components/UI/ThemeToggle/ThemeToggle';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

// Иконка для кнопки
const LockIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        className="bi bi-lock-fill"
        viewBox="0 0 16 16"
        style={{ marginRight: '8px' }}
    >
        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
    </svg>
);

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [useBitrixAuth, setUseBitrixAuth] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        setError('');
        try {
            const response = await fetch(`${PATH_TO_BACKEND}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, useBitrixAuth }),
                credentials: 'include', // Включаем куки в запрос
            });
            const data = await response.json();
            if (data.success) {
                login(data.data.user);
                navigate('/', { replace: true });
            } else {
                setError(data.error?.message || data.error || 'Ошибка входа');
                setIsSubmitting(false);
            }
        } catch (err) {
            setError('Ошибка сервера');
            console.error('Ошибка запроса:', err);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="login-page">
            <ThemeToggle className="login-theme-toggle" />
            <div className="login-container">
                <div className="login-card">
                    <h2 className="login-title">Вход в админ-панель</h2>
                    {error && <div className="login-error">{error}</div>}
                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="username">Логин</label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                placeholder="Введите ваш логин"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">Пароль</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Введите ваш пароль"
                            />
                        </div>
                        <label className="bitrix-auth-toggle">
                            <input
                                type="checkbox"
                                checked={useBitrixAuth}
                                onChange={(e) => setUseBitrixAuth(e.target.checked)}
                            />
                            <span>Войти через Bitrix</span>
                        </label>
                        <button type="submit" className="login-button" disabled={isSubmitting}>
                            <LockIcon />
                            {isSubmitting ? 'Входим...' : 'Войти'}
                        </button>
                    </form>
                </div>
            </div>

            {/* Стили */}
            <style>{`
                .login-page {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background: linear-gradient(135deg, var(--ui-primary-hover), var(--ui-primary));
                    padding: 20px;
                    overflow: hidden;
                }

                .login-theme-toggle {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                }

                .login-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                    max-width: 400px;
                }

                .login-card {
                    background: var(--ui-surface);
                    border-radius: 15px;
                    box-shadow: var(--ui-shadow-lg);
                    padding: 40px 30px;
                    width: 100%;
                    animation: fadeIn 0.5s ease-in-out;
                }

                .login-title {
                    font-size: 24px;
                    font-weight: 600;
                    color: var(--ui-text);
                    text-align: center;
                    margin-bottom: 20px;
                }

                .login-error {
                    background-color: var(--ui-danger-soft);
                    color: var(--ui-danger);
                    padding: 10px 15px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    text-align: center;
                    font-size: 14px;
                }

                .login-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group label {
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--ui-text-muted);
                }

                .form-group input {
                    padding: 12px 15px;
                    border: 1px solid var(--ui-border);
                    border-radius: 8px;
                    font-size: 16px;
                    color: var(--ui-text);
                    background: var(--ui-surface);
                    transition: border-color 0.3s ease, box-shadow 0.3s ease;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: var(--ui-border-focus);
                    box-shadow: var(--ui-shadow-focus);
                }

                .form-group input::placeholder {
                    color: var(--ui-text-faint);
                    font-size: 14px;
                }

                .bitrix-auth-toggle {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: var(--ui-text-muted);
                    font-size: 14px;
                    cursor: pointer;
                    user-select: none;
                }

                .bitrix-auth-toggle input {
                    width: 16px;
                    height: 16px;
                    margin: 0;
                    accent-color: var(--ui-primary);
                    cursor: pointer;
                }

                .login-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 12px;
                    background: linear-gradient(135deg, var(--ui-primary-hover), var(--ui-primary));
                    color: var(--ui-primary-contrast);
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;
                }

                .login-button:hover {
                    transform: translateY(-2px);
                    box-shadow: var(--ui-shadow-md);
                }

                .login-button:active {
                    transform: translateY(0);
                    box-shadow: var(--ui-shadow-sm);
                }

                .login-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                    transform: none;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `}</style>
        </div>
    );
};

export default LoginPage;
